import { apiConfig } from '../config/apiConfig';
import { 
  TimeSheet, 
  TimeEntry, 
  WorkRole, 
  WorkType, 
  ChargeCode, 
  ProjectTicket,
  PatchOperation,
  CreateTimeEntryPayload,
  WorkRolesDict,
  WorkTypesDict,
  ChargeCodesDict,
  CwUser,
  CwUsersByMemberIdDict,
  WorkRoleValidation,
  WorkRoleValidationsList
} from '../types/api.types';

export class BadRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BadRequestError';
  }
}

class ApiService {
  private getTokenFn: (() => Promise<string | null>) | null = null;

  /**
   * Set the function to retrieve the JWT token
   */
  setTokenProvider(fn: () => Promise<string | null>) {
    this.getTokenFn = fn;
  }

  /**
   * Build headers with Authorization token
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) {
        headers['Authorization'] = token;
      }
    }

    return headers;
  }

  /**
   * Generic fetch with pagination support
   */
  private async fetchWithPagination<T>(
    endpoint: string,
    params: Record<string, string> = {}
  ): Promise<T[]> {
    const allData: T[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      const queryParams = new URLSearchParams({
        ...params,
        pagesize: apiConfig.defaultPageSize.toString(),
        page: page.toString(),
      });

      const url = `${apiConfig.baseUrl}${endpoint}?${queryParams}`;
      const headers = await this.getHeaders();

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      allData.push(...(Array.isArray(data) ? data : [data]));

      // Check for next page in header
      const nextPage = response.headers.get('nextpage');
      if (nextPage) {
        page = parseInt(nextPage, 10);
      } else {
        hasNextPage = false;
      }
    }

    return allData;
  }

  /**
   * Fetch time sheets
   */
  async getTimeSheets(conditions?: string): Promise<TimeSheet[]> {
    const params: Record<string, string> = {};
    if (conditions) {
      params.conditions = conditions;
    }
    return this.fetchWithPagination<TimeSheet>(apiConfig.endpoints.timeSheets, params);
  }

  /**
   * Fetch a single time sheet by ID
   */
  async getTimeSheet(sheetId: number): Promise<TimeSheet> {
    const results = await this.getTimeSheets(`id=${sheetId}`);
    if (results.length === 0) {
      throw new Error(`Time sheet ${sheetId} not found`);
    }
    return results[0];
  }

  /**
   * Fetch time entries for a specific time sheet
   */
  async getTimeEntriesBySheet(sheetId: number): Promise<TimeEntry[]> {
    return this.fetchWithPagination<TimeEntry>(apiConfig.endpoints.timeEntries, {
      conditions: `timeSheet/id=${sheetId}`,
    });
  }

  /**
   * Fetch work types and return as dictionary
   */
  async getWorkTypes(): Promise<WorkTypesDict> {
    const data = await this.fetchWithPagination<WorkType>(apiConfig.endpoints.workTypes, {
      conditions: 'inactiveFlag=false',
    });
    return data.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as WorkTypesDict);
  }

  /**
   * Fetch work roles and return as dictionary
   */
  async getWorkRoles(): Promise<WorkRolesDict> {
    const data = await this.fetchWithPagination<WorkRole>(apiConfig.endpoints.workRoles, {
      conditions: 'inactiveFlag=false',
    });
    return data.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as WorkRolesDict);
  }

  /**
   * Fetch charge codes and return as dictionary
   */
  async getChargeCodes(): Promise<ChargeCodesDict> {
    const data = await this.fetchWithPagination<ChargeCode>(apiConfig.endpoints.chargeCodes);
    return data.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as ChargeCodesDict);
  }

  /**
   * Fetch active CW users and return as dictionary keyed by member id
   */
  async getActiveCwUsersByMemberId(): Promise<CwUsersByMemberIdDict> {
    const data = await this.fetchWithPagination<CwUser>(apiConfig.endpoints.cwUsers, {
      conditions: 'inactiveFlag=false',
    });

    return data.reduce((acc, user) => {
      // user.id from cw_users lines up with TimeSheet.member.id
      if (typeof user.id === 'number' && !Number.isNaN(user.id)) {
        acc[user.id] = user;
      }
      return acc;
    }, {} as CwUsersByMemberIdDict);
  }

  /**
   * Search project tickets
   */
  async getWorkRoleValidations(): Promise<WorkRoleValidationsList> {
    const data = await this.fetchWithPagination<WorkRoleValidation>(apiConfig.endpoints.workRoleValidations);
    return data.map((item) => item.workRole);
  }

  async uploadWorkRoleValidations(file: File): Promise<WorkRoleValidationsList> {
    const formData = new FormData();
    formData.append('file', file);

    const headers: HeadersInit = {};
    if (this.getTokenFn) {
      const token = await this.getTokenFn();
      if (token) {
        headers['Authorization'] = token;
      }
    }

    const url = `${apiConfig.baseUrl}/upload_pe_work_types`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 400) {
        const body = await response.text();
        throw new BadRequestError(body || `Upload failed: ${response.status} ${response.statusText}`);
      }
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const data: WorkRoleValidation[] = await response.json();
    return data.map((item) => item.workRole);
  }

  async searchProjectTickets(projectId: string): Promise<ProjectTicket[]> {
    return this.fetchWithPagination<ProjectTicket>(apiConfig.endpoints.projectTickets, {
      conditions: `project/id=${projectId}`,
    });
  }

  /**
   * Update a time entry
   */
  async updateTimeEntry(id: number, operations: PatchOperation[]): Promise<void> {
    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.timeEntries}/${id}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(operations),
    });

    if (!response.ok) {
      if (response.status === 400) {
        const body = await response.text();
        throw new BadRequestError(body || `Failed to update time entry: ${response.status} ${response.statusText}`);
      }
      throw new Error(`Failed to update time entry: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Delete a time entry
   */
  async deleteTimeEntry(id: number): Promise<void> {
    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.timeEntries}/${id}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete time entry: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Reverse a time entry
   */
  async reverseTimeEntry(entryId: number, comment: string): Promise<void> {
    const url = `${apiConfig.baseUrl}/cw_reverse_time_entry/${entryId}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ comment }),
    });

    if (!response.ok) {
      let errorMessage = `Failed to reverse time entry: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage = errorBody;
      } catch { /* ignore */ }
      throw new Error(errorMessage);
    }
  }

  /**
   * Reverse a time sheet
   */
  async reverseTimeSheet(sheetId: number): Promise<void> {
    const url = `${apiConfig.baseUrl}/cw_reverse_time_sheet/${sheetId}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      let errorMessage = `Failed to reverse time sheet: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage = errorBody;
      } catch { /* ignore */ }
      throw new Error(errorMessage);
    }
  }

  /**
   * Submit a time sheet
   */
  async submitTimeSheet(sheetId: number): Promise<void> {
    const url = `${apiConfig.baseUrl}/cw_submit_time_sheet/${sheetId}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      let errorMessage = `Failed to submit time sheet: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) errorMessage = errorBody;
      } catch { /* ignore */ }
      throw new Error(errorMessage);
    }
  }

  /**
   * Create a new time entry
   */
  async createTimeEntry(payload: CreateTimeEntryPayload): Promise<TimeEntry> {
    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.timeEntries}`;
    const headers = await this.getHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = `Failed to create time entry: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.text();
        if (errorBody) {
          errorMessage = errorBody;
        }
      } catch {
        // Could not read error response
      }
      if (response.status === 400) {
        throw new BadRequestError(errorMessage);
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

// Export singleton instance
export const apiService = new ApiService();
